import { Headline, SearchField, Slider, TextButton } from '@oneaudi/unified-web-alpha-components';
import { useEffect, useRef, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import {
	CMS_DEFAULT_STOCKCAR_RANGE_MAX,
	CMS_DEFAULT_STOCKCAR_RANGE_MIN,
	CMS_DEFAULT_STOCKCAR_RANGE_STEP,
} from '~/app/models/constans';
import { useStockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { GeoData } from '~/app/pages/configurator/components/dialogs/stock-car-dialog/models/types';
import {
	PlaceIndex,
	useLocationSearch,
} from '~/app/pages/configurator/components/dialogs/stock-car-dialog/useLocationSearch';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useFootnotes } from '~/components/footnotes';
import { PRICE_DISCLAIMER_REMARK, STOCK_CAR_REMARK } from '~/components/footnotes/constants';
import { StyledLocationDialog } from '../styles/styled-location-dialog';

interface props {
	location: string;
	onGeoDataChange: (newGeoData: GeoData) => void;
	radius: number;
	onRadiusChange: (newRadius: number) => void;
	onLocationChange: (newLocation: string) => void;
}

export default function UserLocationView({
	location,
	onGeoDataChange,
	radius,
	onRadiusChange,
	onLocationChange,
}: props) {
	const { config } = useMarketConfig();
	const { setLocationView } = useStockCarDialog(config.stockCarConfig.geoFilter);
	const [locationTerm, setLocationTerm] = useState<string>(location ?? '');
	const [foundLocations, setFoundLocations] = useState<PlaceIndex[] | undefined>([]);
	const [searchingLocation, setSearchingLocation] = useState<boolean>(false);
	const [userLocation, setUserLocation] = useState('');
	const [userLocationError, setUserLocationError] = useState('');
	const [loadNewResults, setLoadNewResults] = useState(true);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const { i18n } = useI18n();
	const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
	const { removeFootnote } = useFootnotes();

	const { getPlaceIdsBySearchTerm, getCoordinatesByPlaceId, getLocationByCoordinates } = useLocationSearch();
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const getLocations = async () => {
			try {
				if (loadNewResults && locationTerm) {
					setFoundLocations(await getPlaceIdsBySearchTerm(locationTerm));
				}
			} catch (error) {
				console.error(error);
			}
		};
		if (debounceTimeout) {
			clearTimeout(debounceTimeout);
		}

		if (locationTerm) {
			setDebounceTimeout(
				setTimeout(() => {
					getLocations();
				}, 500),
			);
		}

		return () => {
			if (debounceTimeout) {
				clearTimeout(debounceTimeout);
			}
		};
	}, [locationTerm]);

	useEffect(() => {
		const input = inputRef.current;

		// Set static ARIA attributes
		input?.setAttribute('role', 'combobox');
		input?.setAttribute('aria-autocomplete', 'list');
		input?.setAttribute('aria-controls', 'suggestion-list');
		input?.setAttribute('aria-haspopup', 'listbox');
		input?.setAttribute('aria-expanded', String(!!(foundLocations?.length && locationTerm)));

		// Dynamically update aria-activedescendant based on highlighted item
		if (highlightedIndex >= 0 && foundLocations?.[highlightedIndex]) {
			input?.setAttribute('aria-activedescendant', `suggestion-${foundLocations[highlightedIndex].PlaceId}`);
		} else {
			input?.removeAttribute('aria-activedescendant');
		}

		const handleKeyDown = (e: KeyboardEvent) => {
			if (!foundLocations || foundLocations.length === 0) return;

			switch (e.key) {
				case 'ArrowDown':
					setHighlightedIndex((prev) => (prev + 1) % foundLocations.length);
					break;
				case 'ArrowUp':
					setHighlightedIndex((prev) => (prev - 1 + foundLocations.length) % foundLocations.length);
					break;
				case 'Enter':
					if (highlightedIndex >= 0) {
						onSuggestionClick(foundLocations[highlightedIndex]);
					}
					break;
				case 'Escape':
					e.preventDefault();
					setLocationTerm('');
					setFoundLocations([]);
					setHighlightedIndex(-1);
					break;
				default:
			}
		};

		input?.addEventListener('keydown', handleKeyDown);
		return () => {
			input?.removeEventListener('keydown', handleKeyDown);
		};
	}, [foundLocations, highlightedIndex, locationTerm]);

	const getClientLocation = () => {
		if (!navigator.geolocation) {
			setUserLocationError(i18n('ngc.stockcar.location.automatic.unsupported'));
			return;
		}
		setSearchingLocation(true);
		navigator.geolocation.getCurrentPosition(
			(data) => {
				onGeoDataChange({ lat: data.coords.latitude, lng: data.coords.longitude });
				searchLocationByCoords(data.coords.latitude, data.coords.longitude);
				setSearchingLocation(false);
			},
			(error: GeolocationPositionError) => {
				setSearchingLocation(false);
				error.code === 1 && setUserLocationError(i18n('ngc.stockcar.location.automatic.denied'));
			},
		);
	};

	const handleInput = (value: string) => {
		// don't trigger search again when user clicks on search item in drop down
		userLocationError && setUserLocationError('');
		if (!loadNewResults) {
			setLoadNewResults(true);
		} else {
			setLocationTerm(value);
		}
	};

	const searchLocationByCoords = async (lat: number, long: number) => {
		const result = await getLocationByCoordinates([lat, long]);
		if (result) {
			const { label, coordinates } = result;
			setLoadNewResults(false);
			onLocationChange(label);
			setFoundLocations([]);
			setLocationTerm(label);
			setUserLocation(label);
			onGeoDataChange(coordinates);
		}
	};

	const onSuggestionClick = async ({ PlaceId, Text }: { PlaceId: string; Text: string }) => {
		const result = await getCoordinatesByPlaceId(PlaceId);
		if (result) {
			setLoadNewResults(false);
			onLocationChange(Text);
			setFoundLocations([]);
			setLocationTerm(Text);
			onGeoDataChange(result?.coordinates);
		}
	};

	const handleClick = () => {
		setLocationView('StockCarView');
		appTrackingService.summaryClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: `configurator (stock car matching) - click to apply filters`,
			componentName: 'configurator-stock-car-matching',
			label: i18n('ngc.stockcar.matches.open'),
			elementName: 'button',
			value: location === userLocation ? 'current location' : 'manual search',
			pos: `${radius}km`,
		});
	};

	removeFootnote(STOCK_CAR_REMARK);
	removeFootnote(PRICE_DISCLAIMER_REMARK);

	return (
		<StyledLocationDialog>
			<Headline
				tag={'h1'}
				className="value"
				variant={{ type: 'headline', order: '2', weight: 'normal' }}
				margin={[0, 0, '24px', 0]}>
				{i18n('ngc.stockcar.location.title')}
			</Headline>
			<div className="search-container">
				<SearchField
					ref={inputRef}
					hint={userLocationError || undefined}
					id="search-location"
					value={locationTerm}
					disabled={searchingLocation}
					label={i18n('ngc.stockcar.location.search')}
					clearButton={{
						onClick: () => {
							setLocationTerm('');
							onLocationChange('');
						},
						'aria-label': i18n('ngc.a11y.clear'),
					}}
					onChange={(event) => handleInput(event.target.value)}
					additionalIconButton={{
						icon: 'gps',
						onClick: getClientLocation,
						'aria-label': i18n('ngc.stockcar.location.automatic'),
					}}
				/>
				{locationTerm && loadNewResults && foundLocations && foundLocations?.length > 0 && (
					<ul className="suggestions-container" role="listbox">
						{foundLocations && foundLocations?.length > 0
							? foundLocations?.map((item, index) => (
									<li
										id={`suggestion-${item.PlaceId}`}
										aria-label={item.Text}
										onClick={() => onSuggestionClick(item)}
										key={item.PlaceId}
										role="option"
										aria-selected={index === highlightedIndex}
										onMouseEnter={() => setHighlightedIndex(index)}>
										{item.Text}
									</li>
								))
							: !location && <li>{i18n('ngc.stockcar.location.notfound')}</li>}
					</ul>
				)}
			</div>
			<Slider
				margin={['32px', 0, '24px', 0]}
				id="range-slider"
				label={i18n('ngc.stockcar.location.radius')}
				max={config.stockCarConfig.rangeFilter?.max || CMS_DEFAULT_STOCKCAR_RANGE_MAX}
				min={config.stockCarConfig.rangeFilter?.min || CMS_DEFAULT_STOCKCAR_RANGE_MIN}
				step={config.stockCarConfig.rangeFilter?.step || CMS_DEFAULT_STOCKCAR_RANGE_STEP}
				valueFormat={(value) => `${value} km`}
				onChange={(nextRange) => onRadiusChange(nextRange as number)}
				value={radius}
			/>
			<TextButton disabled={!location} isFullWidth onClick={handleClick} variant="primary">
				{i18n('ngc.stockcar.matches.open')}
			</TextButton>
		</StyledLocationDialog>
	);
}
